import { normalizeLocale, getCurrentLocale as getLocaleFromController, parseLocalePath } from '~/utils/localeController'

/**
 * 从当前页面 host 获取 domain，并提供统一的 X-Domain header 注入方法。
 * biz 接口通过 X-Domain 识别租户，不需要 siteId / token / templateId。
 */

const normalizeLang = (value: unknown): string => {
  return normalizeLocale(value)
}

const firstHeaderValue = (headers: Record<string, string | string[] | undefined>, keys: string[]) => {
  for (const key of keys) {
    const value = headers[key]
    if (Array.isArray(value)) return String(value[0] ?? '')
    if (value != null) return String(value)
  }
  return ''
}

const getRoutePath = () => {
  if (import.meta.client) {
    try {
      return `${window.location.pathname}${window.location.search}${window.location.hash}` || '/'
    } catch {
      return ''
    }
  }

  try {
    return useRoute().fullPath || '/'
  } catch {
    return ''
  }
}

const getRouteLocale = () => {
  const routePath = getRoutePath()
  return routePath ? parseLocalePath(routePath).locale || '' : ''
}

export const getDomain = (): string => {
  if (import.meta.server) {
    const headers = useRequestHeaders(['x-forwarded-host', 'host'])
    const raw = String(headers['x-forwarded-host'] || headers['host'] || '')
    const host = raw.split(',')[0]?.trim() || ''
    return host.replace(/:\d+$/, '')
  }

  if (import.meta.client) {
    try {
      return window.location.host
    } catch {
      return ''
    }
  }

  return ''
}

export const getCurrentPageLang = (): string => {
  if (import.meta.server) {
    const routeLocale = getRouteLocale()
    if (routeLocale) return normalizeLang(routeLocale)

    const headers = useRequestHeaders(['x-soho-route-lang', 'lang'])
    return normalizeLang(firstHeaderValue(headers, ['x-soho-route-lang', 'lang']) || 'en')
  }

  if (import.meta.client) {
    try {
      return getLocaleFromController()
    } catch {
      return 'en'
    }
  }

  return 'en'
}

export const withDomainHeaders = (
  headers?: Record<string, string | string[] | undefined>,
): Record<string, string> => {
  const domain = getDomain()
  const result: Record<string, string> = {}

  for (const [key, value] of Object.entries(headers || {})) {
    if (value == null) continue
    result[key] = Array.isArray(value) ? String(value[0] ?? '') : String(value)
  }

  if (domain && !result['X-Domain'] && !result['x-domain']) {
    result['X-Domain'] = domain
  }

  if (!result['x-soho-route-path'] && !result['X-Soho-Route-Path']) {
    const routePath = getRoutePath()
    if (routePath) result['x-soho-route-path'] = routePath
  }

  const explicitLang = firstHeaderValue(result, ['x-soho-route-lang', 'X-Soho-Route-Lang', 'lang', 'Lang'])
  const pageLang = normalizeLang(explicitLang || getCurrentPageLang())
  result.lang = pageLang
  result['x-soho-route-lang'] = pageLang
  return result
}
